/* @jsxRuntime automatic */
/* @jsxImportSource @superweb/css */

import { useState } from "react";
import { getCountryCallingCode, type CountryCode } from "libphonenumber-js";

import { useLocale } from "@superweb/intl";
import {
  createPhoneFieldState,
  createTextFieldState,
  ExperimentalFormRow,
  PhoneField,
  TextField,
  type PhoneFieldState,
  type TextFieldState,
} from "@superweb/ui";
import { useFlag } from "@superweb/flags";

import { useAction, type ApiResponse } from "@fleet/api";

import { useLogAction } from "#internal/analytics/logActions";
import { useNavigate } from "#internal/router-new";
import { PhoneVerificator } from "#internal/ui";
import { useMessage } from "#internal/intl";
import {
  useErrorMessages,
  useOnChangePartial,
  useOnShowErrors,
  useSubmitErrorMessages,
  useValidate,
} from "#internal/utils";

import { getSteps } from "../steps";
import { RegisterForm } from "./form";

type PersonalFormState = {
  phone: PhoneFieldState;
  first_name: TextFieldState;
  last_name: TextFieldState;
};
type FormRequirementItem = "required" | "optional" | "none";
type FormRequirements = {
  license: FormRequirementItem;
  vehicle: FormRequirementItem;
  region: FormRequirementItem;
};

export const MultipleFlowContent = ({
  refId,
  parkInfo,
  sessionId,
  isDelivery,
  formErrorMessage,
  setFormErrorMessage,
  profession,
  employmentType,
  isMultipleProfessions,
  isMultipleEmploymentTypes,
  isIframe,
}: {
  refId: string;
  sessionId: string;
  isDelivery: boolean;
  formErrorMessage?: string;
  setFormErrorMessage: (errorMessage: string) => void;
  parkInfo: Extract<
    ApiResponse<"/fleet-forms/selfreg/v1/park/info", "GET">,
    { status: 200 }
  >["body"];
  profession?: {
    profession_id: string;
    employment_type_list?: {
      employment_type:
        | "selfemployed"
        | "individual_entrepreneur"
        | "park_employee";
    }[];
    form_requirements: FormRequirements;
  };
  employmentType?: "selfemployed" | "individual_entrepreneur" | "park_employee";
  isMultipleProfessions?: boolean;
  isMultipleEmploymentTypes?: boolean;
  isIframe: boolean;
}) => {
  const locale = useLocale();
  const message = useMessage();
  const flag = useFlag();
  const enableRemoveDeprecatedFields = flag({
    id: "84e3e48d-6212-40f9-af46-6575de66f85d",
    description: "Enable remove deprecated fields",
  });
  const navigate = useNavigate();

  const logActionInvalid = useLogAction("invalid");
  const logActionSubmit = useLogAction("submit");

  const { action: actionLead, isFetching: isFetchingLead } = useAction({
    url: "/fleet-forms/selfreg/v1/lead",
    method: "POST",
  });

  const { nextStep } = getSteps({
    isMultipleProfessions,
    isMultipleEmploymentTypes,
    employmentType,
    formRequirements: enableRemoveDeprecatedFields
      ? profession?.form_requirements
      : parkInfo.form_requirements,
    currentStep: "register",
  });

  const { validateTextField, validatePhoneField } = useValidate();
  const errorMessages = useErrorMessages();
  const submitErrorMessages = useSubmitErrorMessages(parkInfo.country_code);

  const onError = (code?: string) => {
    if (code) {
      setFormErrorMessage(submitErrorMessages[code] ?? errorMessages.default);
    } else {
      setFormErrorMessage(errorMessages.default);
    }
  };

  const countryCode = getCountryCallingCode(
    parkInfo.country_code as CountryCode,
  );

  const [confirmationId, setConfirmationId] = useState("");
  const [personalState, setPersonalState] = useState<PersonalFormState>(() => ({
    phone: createPhoneFieldState({
      value: countryCode ? "+" + countryCode : "",
      errorMessage: countryCode
        ? errorMessages.invalidPhone
        : errorMessages.requiredField,
    }),
    first_name: createTextFieldState({
      value: "",
      errorMessage: errorMessages.requiredField,
    }),
    last_name: createTextFieldState({
      value: "",
      errorMessage: errorMessages.requiredField,
    }),
  }));

  const onChangePersonalPartial = useOnChangePartial(setPersonalState);
  const onShowPersonalErrors = useOnShowErrors(setPersonalState);

  const enableTestPhones = flag({
    id: "3c8e074f-189c-4322-98d7-df3a553ccc61",
    description: "Enable test phones for testing",
  });
  const enablePhoneVerification = Boolean(parkInfo.phone_verification);

  return (
    <RegisterForm
      isIframe={isIframe}
      isMultipleProfessions={isMultipleProfessions}
      isMultipleEmploymentTypes={isMultipleEmploymentTypes}
      profession={profession}
      content={
        <>
          <ExperimentalFormRow columnsCount={enablePhoneVerification ? 2 : 1}>
            <PhoneField
              required
              label={message({
                id: "7a59793f-6006-4238-8ccc-7ee7ff616ee5",
                context: "Yango forms. Phone number field",
                default: "Phone number",
              })}
              state={personalState.phone}
              onChange={(state) => {
                setConfirmationId("");
                const errorMessage = validatePhoneField(state.value, {
                  isPhoneInvalid: enableTestPhones
                    ? state.invalid && !state.value.match(/^\+7000[0-9]{7}$/)
                    : state.invalid,
                });
                onChangePersonalPartial({
                  phone: {
                    ...state,
                    errorMessage,
                  },
                });
              }}
            />
            {enablePhoneVerification && (
              <PhoneVerificator
                phone={
                  !personalState.phone.invalid &&
                  !personalState.phone.errorMessage
                    ? personalState.phone.value
                    : ""
                }
                state={confirmationId}
                onChange={setConfirmationId}
              />
            )}
          </ExperimentalFormRow>
          <ExperimentalFormRow columnsCount={2}>
            <TextField
              required
              state={personalState.first_name}
              label={message({
                id: "092fcb49-8236-49a8-a42c-f363cd70a5c4",
                context: "Yango form. Name field",
                default: "First name",
              })}
              onChange={(state) => {
                const errorMessage = validateTextField(state.value);
                onChangePersonalPartial({
                  first_name: {
                    ...state,
                    errorMessage,
                    errorVisible: Boolean(errorMessage),
                  },
                });
              }}
            />
            <TextField
              required
              state={personalState.last_name}
              label={message({
                id: "25d07a59-dbae-44e2-8dd5-257aab208c8e",
                context: "Yango forms. Surname field",
                default: "Last name",
              })}
              onChange={(state) => {
                const errorMessage = validateTextField(state.value);
                onChangePersonalPartial({
                  last_name: {
                    ...state,
                    errorMessage,
                    errorVisible: Boolean(errorMessage),
                  },
                });
              }}
            />
          </ExperimentalFormRow>
        </>
      }
      onSubmit={() => {
        onShowPersonalErrors();

        const hasErrors = Object.keys(personalState).some(
          (field) =>
            personalState[field as keyof PersonalFormState].errorMessage !==
            undefined,
        );

        if (hasErrors) {
          if (personalState.phone.errorMessage === errorMessages.invalidPhone) {
            logActionInvalid("invalid_phone_number", {
              onlyYM: true,
            });
          }
          return;
        }

        const searchParams = new URLSearchParams({
          ref_id: refId,
          ...(profession
            ? { profession_id: profession.profession_id }
            : undefined),
          ...(employmentType
            ? { employment_type_id: employmentType }
            : undefined),
          ...(sessionId ? { sessionId } : undefined),
          ...(isMultipleProfessions
            ? { is_multiple_professions: "true" }
            : undefined),
          ...(isMultipleEmploymentTypes
            ? { is_multiple_employment_types: "true" }
            : undefined),
          ...(confirmationId ? { confirmation_id: confirmationId } : undefined),
        });

        actionLead(
          {
            headers: {
              "x-submission-id": sessionId,
              language: locale.toString(),
              "content-type": "application/json",
            },
            query: {
              ref_id: refId,
            },
            body: {
              first_name: personalState.first_name.value,
              last_name: personalState.last_name.value,
              phone: personalState.phone.value,
              agree_processing_data: true,
              agree_receive_info: true,
              confirmation_id: confirmationId ? confirmationId : undefined,
            },
          },
          {
            onResponse: (res) => {
              if (res.status === 200) {
                logActionSubmit("submit_form", {
                  params: "no_auth",
                });

                const leadId = res.body.id;
                searchParams.set("lead_id", leadId);
                navigate(`/${nextStep}?${searchParams}`);

                return;
              }

              switch (res.body?.code) {
                case "invalid_phone_number": {
                  logActionInvalid("invalid_phone_number", {
                    onlyYM: true,
                  });
                  onChangePersonalPartial({
                    phone: {
                      ...personalState.phone,
                      errorMessage: errorMessages.invalidPhone,
                      errorVisible: true,
                    },
                  });
                  break;
                }
                default: {
                  onError(res.body?.code);
                  break;
                }
              }
            },
          },
        );
      }}
      isLoading={isFetchingLead}
      isDelivery={isDelivery}
      formErrorMessage={formErrorMessage}
      setFormErrorMessage={setFormErrorMessage}
      parkInfo={parkInfo}
    />
  );
};
